import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Logo } from "@/components/Logo";
import { hourlyRate } from "@/pages/Booking";
import { api } from "@/lib/api";

const PACKAGES = {
  weddings: {
    overline: "Weddings & Occasions",
    title: "Arrive like the",
    italic: "main event.",
    image: "/images/escalade-wedding-daylight.png",
    intro:
      "Ribboned and spotless, with a chauffeur who has rehearsed the route and the timings before you ask. Every wedding charter includes a dedicated concierge, chilled water, and unhurried waiting time at the venue.",
    tiers: [
      {
        name: "The Ceremony",
        hours: 3,
        addons: ["ribbon"],
        detail: "Collection, ceremony, and photographs. Ribbon & florals dressed to your palette.",
      },
      {
        name: "The Celebration",
        hours: 5,
        addons: ["ribbon", "champagne"],
        detail: "Ceremony through reception. Ribbon & florals plus champagne on ice for the couple.",
      },
      {
        name: "The Full Day",
        hours: 8,
        addons: ["ribbon", "champagne", "red_carpet"],
        detail: "Morning preparations to the last dance — ribbon, champagne, and a red carpet arrival.",
      },
    ],
  },
  corporate: {
    overline: "Corporate & Roadshows",
    title: "Eleven meetings,",
    italic: "one standard.",
    image: "/images/bmw-xm-jet-front.png",
    intro:
      "A roadshow is a sequence of moments that cannot slip. Your chauffeur stages early at every stop, the cabin is a quiet office between them, and your concierge tracks flights and timings in the background.",
    tiers: [
      {
        name: "Half-Day Roadshow",
        hours: 4,
        addons: [],
        detail: "Four hours at your direction across South Florida — staged, silent, on the minute.",
      },
      {
        name: "Full-Day Roadshow",
        hours: 8,
        addons: [],
        detail: "The full working day with onboard Wi-Fi, refreshments and flight tracking on request.",
      },
      {
        name: "Airport Programme",
        hours: 2,
        addons: ["meet_greet"],
        detail: "Recurring MIA · PBI · FBO transfers with meet & greet boards for visiting principals.",
      },
    ],
  },
};

export default function Packages() {
  const { slug } = useParams();
  const pack = slug ? PACKAGES[slug] : null;
  const [addonPrices, setAddonPrices] = useState({});

  useEffect(() => {
    api
      .get("/addons")
      .then((r) => setAddonPrices(Object.fromEntries((r.data.addons || []).map((a) => [a.key, a.price]))))
      .catch(() => {});
  }, []);

  const tierTotal = (t) => {
    const base = hourlyRate(t.hours) * t.hours;
    return base + t.addons.reduce((sum, k) => sum + (addonPrices[k] ?? 0), 0);
  };
  const pricesLoaded = Object.keys(addonPrices).length > 0;

  if (pack) {
    return (
      <main className="min-h-screen bg-[#0F1012] grain" data-testid={`package-page-${slug}`}>
        <header className="border-b border-[#2E3340]">
          <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
            <Logo />
            <Link
              to="/packages"
              className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-400 hover:text-amber-400 transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft size={12} /> All packages
            </Link>
          </div>
        </header>

        <div className="relative h-[42vh] min-h-72 overflow-hidden">
          <img src={pack.image} alt={pack.overline} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1012] via-[#0F1012]/40 to-transparent" />
          <div className="absolute bottom-0 inset-x-0">
            <div className="max-w-6xl mx-auto px-6 pb-10">
              <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.4em] text-amber-500">{pack.overline}</p>
              <h1 className="font-serif-lux font-normal text-4xl sm:text-5xl lg:text-6xl text-amber-50 mt-3 leading-tight">
                {pack.title} <span className="gold-text italic">{pack.italic}</span>
              </h1>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-14">
          <p className="text-sm text-zinc-300 leading-relaxed max-w-2xl">{pack.intro}</p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            {pack.tiers.map((t, i) => {
              const params = new URLSearchParams({ mode: "hourly", hours: String(t.hours), package: slug });
              if (t.addons.length) params.set("addons", t.addons.join(","));
              return (
                <motion.div
                  key={t.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className="border border-[#2E3340] bg-[#1E2128] p-7 flex flex-col hover:border-[#C5A059]/60 transition-colors duration-500"
                  data-testid={`package-tier-${i}`}
                >
                  <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-500">
                    {t.hours} hour{t.hours > 1 ? "s" : ""} · by the hour
                  </p>
                  <h2 className="font-serif-lux text-2xl text-amber-50 mt-2">{t.name}</h2>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed flex-1">{t.detail}</p>
                  <p className="mt-5 font-serif-lux text-3xl gold-text" data-testid={`package-price-${i}`}>
                    from ${pricesLoaded || !t.addons.length ? tierTotal(t) : hourlyRate(t.hours) * t.hours}
                    <span className="block font-mono-lux text-[9px] font-semibold uppercase tracking-[0.2em] text-zinc-500 mt-1">
                      {t.addons.length
                        ? "all-in — charter + listed add-ons included"
                        : "charter fare · add-ons available at booking"}
                    </span>
                  </p>
                  <Link
                    to={`/book?${params.toString()}`}
                    data-testid={`package-reserve-${i}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 font-mono-lux text-[10px] font-bold uppercase tracking-[0.25em] px-6 py-3.5 bg-amber-500 text-black hover:bg-amber-300 transition-colors"
                  >
                    Reserve this package <ArrowRight size={12} />
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <p className="mt-10 text-xs text-zinc-500 leading-relaxed max-w-2xl">
            Every package is tailored before confirmation — timings, venues, florals, security. Call the concierge at
            +1 (786) 874-0320 or email concierge@aureusluxury.com for anything beyond the page.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0F1012] grain" data-testid="packages-page">
      <header className="border-b border-[#2E3340]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo />
          <Link
            to="/"
            className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-400 hover:text-amber-400 transition-colors"
          >
            ← Back
          </Link>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.4em] text-amber-500">Packages</p>
        <h1 className="font-serif-lux font-normal text-4xl sm:text-5xl text-amber-50 mt-3">
          Occasions, <span className="gold-text italic">composed.</span>
        </h1>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(PACKAGES).map(([key, p], i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="group relative border border-[#2E3340] overflow-hidden"
              data-testid={`package-card-${key}`}
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.overline}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-7 bg-[#1E2128]">
                <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-500">{p.overline}</p>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed line-clamp-2">{p.intro}</p>
                <Link
                  to={`/packages/${key}`}
                  data-testid={`package-open-${key}`}
                  className="mt-5 inline-flex items-center gap-2 font-mono-lux text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300 hover:text-amber-100 transition-colors"
                >
                  View packages <ArrowRight size={12} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
